import { DEFAULT_LANG, localeTagFor, normalizeLang } from "@/i18n/locale";

// Duration / countdown formatters for Agenda Mini-Mirror hero block.
//
// Locale-aware via i18n `t` injection (avoids importing Intl/dayjs heavy deps).
// Caller passes a tiny `t(key)` function — same shape used by LanguageProvider.

// Aqui existiam 17 strings por idioma escritas à mão (12 meses + hoje/amanhã/
// dia/dias/min) sob um `type Lang` LOCAL — uma segunda união de idiomas que o
// compilador não cruzava com a do app. Com seis idiomas seriam 102 strings, e
// nada garantiria que a lista local acompanhasse LANGUAGES.
//
// Intl.RelativeTimeFormat e Intl.DateTimeFormat entregam tudo isso a partir do
// localeTag que a tabela central já publica. Mesma troca feita em
// _shared/datetime-natural.ts, pelo mesmo motivo.

/** hoje / amanhã / ontem no idioma dado. */
function relativeDayWord(days: number, lang: string): string {
  return new Intl.RelativeTimeFormat(localeTagOf(lang), { numeric: "auto" }).format(days, "day");
}

/** Aceita o Lang do app ou um BCP-47; cai no padrão do produto se não der. */
function localeTagOf(lang: string | undefined): string {
  const known = normalizeLang(lang ?? null);
  if (known) return localeTagFor(known);
  try {
    new Intl.DateTimeFormat(lang as string);
    return lang as string;
  } catch {
    return localeTagFor(DEFAULT_LANG);
  }
}

/** Complete localized relative phrase; future dates use calendar days, not elapsed 24h buckets.
 * Returns a dash for missing, invalid or past timestamps.
 */
export function formatCountdown(targetIso: string | null | undefined, lang: string): string {
  if (!targetIso) return "—";
  const target = new Date(targetIso).getTime();
  if (Number.isNaN(target)) return "—";
  const diffMs = target - Date.now();
  if (diffMs <= 0) return "—";

  const diffMin = Math.max(1, Math.ceil(diffMs / 60000));
  const relative = new Intl.RelativeTimeFormat(localeTagOf(lang), { numeric: "auto", style: "short" });
  if (diffMin < 60) return relative.format(diffMin, "minute");
  const now = new Date();
  const date = new Date(target);
  const dayDistance = Math.round((Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) - Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);
  if (dayDistance === 0) return relative.format(Math.ceil(diffMin / 60), "hour");
  if (dayDistance < 7) return relativeDayWord(dayDistance, lang);
  return new Intl.DateTimeFormat(localeTagOf(lang), { day: "numeric", month: "short", year: "numeric" }).format(date);
}

/**
 * Format event meta line: "14:30 · Sala 3 · 1h" (location + duration optional).
 * Returns empty string components are gracefully omitted.
 */
export function formatEventMeta(
  startIso: string,
  endIso: string | null | undefined,
  location: string | null | undefined,
): string {
  const start = new Date(startIso);
  const hh = String(start.getHours()).padStart(2, "0");
  const mm = String(start.getMinutes()).padStart(2, "0");
  const parts: string[] = [`${hh}:${mm}`];
  if (location && location.trim()) parts.push(location.trim());
  if (endIso) {
    const end = new Date(endIso);
    const durMs = end.getTime() - start.getTime();
    if (durMs > 0) {
      const durMin = Math.round(durMs / 60000);
      const durH = Math.floor(durMin / 60);
      const remMin = durMin - durH * 60;
      if (durH === 0) parts.push(`${durMin}min`);
      else if (remMin === 0) parts.push(`${durH}h`);
      else parts.push(`${durH}h ${remMin}m`);
    }
  }
  return parts.join(" · ");
}

/**
 * "CALENDAR · GOOGLE" / "CALENDAR · APPLE" / "MIRROR" / "MANUAL".
 * external_provider takes precedence (Google/Apple/Outlook); source fallback
 * surfaces native VYV origins (mirror/voice/manual).
 */
export function formatEventSource(
  externalProvider: string | null | undefined,
  source: string | null | undefined,
): string {
  if (externalProvider && externalProvider.trim()) {
    return `CALENDAR · ${externalProvider.trim().toUpperCase()}`;
  }
  if (source && source.trim()) {
    return source.trim().toUpperCase();
  }
  return "MIRROR";
}

/**
 * Format weekly volume (kg) — locale-aware thousand separator + " kg" suffix.
 *   formatVolumeKg(1830, "PT-BR") → "1.830 kg"
 *   formatVolumeKg(1830, "EN")    → "1,830 kg"
 *   formatVolumeKg(1830, "NL")    → "1.830 kg"
 *   formatVolumeKg(0, *)          → "0 kg"
 *
 * Used by Fitness Mini-Mirror VOLUME SEMANAL hero.
 */
export function formatVolumeKg(kg: number, lang: string): string {
  const L = normalizeLang(lang);
  const safe = Number.isFinite(kg) ? Math.round(kg) : 0;
  const localeTag = L === "PT-BR" ? "pt-BR" : L === "NL" ? "nl-NL" : "en-US";
  const formatted = new Intl.NumberFormat(localeTag, { maximumFractionDigits: 0 }).format(safe);
  return `${formatted} kg`;
}
